import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Dialog,
  Stack,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  useTheme
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import Checkbox from 'packages/ui/Checkbox'
import { useUser } from 'store'
import { BYPASSES_DISCLAIMER_KEY } from 'variables'

import { useSelection } from '../store'
import { IBypass } from '../use-bypass'
import { useDeactivateDisclaimer } from '../use-actions'

const HeaderActivate = ({ onClose }) => {
  const theme = useTheme()
  const [selection] = useSelection()
  const {t} = useTranslation()
  return (
    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
      <Stack direction={'row'} gap={1}>
        <Stack
          justifyContent={'center'}
          alignItems={'center'}
          pl={.5}
          sx={{
            width: 32,
            height: 32,
            borderRadius: 16,
            backgroundColor: '#DBE8FE',
            color: theme.palette.primary.main
          }}
        >
          <i className={'fa-light fa-play'} style={{ fontSize: 14, fontWeight: 500 }} />
        </Stack>
        <Stack>
          <Typography component={'span'} sx={{ color: theme.palette.grey[500], fontWeight: 500 }}>
            {
              t('bypass.modal.activation')
            }
          </Typography>
          <Typography component={'span'} sx={{ fontWeight: 500 }}>
            {
              t('bypass.card_in_mass.count', { count: selection.length || 1 })
            }
          </Typography>
        </Stack>
      </Stack>
      <i className={'fa-light fa-xmark'} style={{ fontSize: 14, cursor: 'pointer', alignSelf: 'start' }} onClick={onClose} />
    </Stack>
  )
}

const Header = ({ item }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  return (
    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
      <Stack direction={'row'} gap={1}>
        <Stack
          justifyContent={'center'}
          alignItems={'center'}
          sx={{
            width: 32,
            height: 32,
            borderRadius: 16,
            backgroundColor: '#DBE8FE',
            color: theme.palette.primary.main
          }}
        >
          <i className={`fa-light fa-${item ? 'pen' : 'plus'}`} style={{ fontSize: 14, fontWeight: 500 }} />
        </Stack>
        <Stack>
          <Typography component={'span'} sx={{ color: theme.palette.grey[500], fontWeight: 500 }}>
            {
              item
                ? t('bypass.modal.global.update')
                : t('bypass.modal.global.create')
            }
          </Typography>
          <Typography component={'span'} sx={{ fontWeight: 500 }}>
            {
              item
                ? item.mac_address
                : t('bypass.modal.global.new_bypass')
            }
          </Typography>
        </Stack>
      </Stack>
      <i className={'fa-light fa-xmark'} style={{ fontSize: 14, cursor: 'pointer', alignSelf: 'start' }} onClick={() => navigate('..')} />
    </Stack>
  )
}

const Disclaimer = ({ onConfirm }) => {
  const [checked, setChecked] = useState(false)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [deactivateDisclaimer, { loading }] = useDeactivateDisclaimer()

  const handleDeactivateDisclaimer = async () => {
    if (!checked) {
      onConfirm()
      return
    }
    const response = await deactivateDisclaimer()
    console.log(response)
    if (response.error) {
      return
    }
    onConfirm()
  }

  return (
    <Stack>
      <Box sx={{ fontSize: 14, color: 'content.secondary' }} dangerouslySetInnerHTML={{ __html: t('bypass.modal.global.disclaimer.message') }} />
      <Stack direction={'row'} justifyContent='space-between' alignItems={'center'} mt={4}>
        <Stack direction={'row'} gap={1} alignItems={'center'}>
          <Checkbox checked={checked} onChange={() => setChecked(!checked)} />
          <Typography sx={{ fontSize: 14, color: 'content.secondary', cursor: 'pointer' }} component={'span'} onClick={() => setChecked(!checked)}>
            {t('bypass.modal.global.disclaimer.checkbox')}
          </Typography>
        </Stack>
        <Stack gap={2} direction={'row'} alignSelf={'end'}>
          <Button size={'small'} variant={'outlined'} onClick={() => navigate('..')}>
            {t('ui_elements.cancel')}
          </Button>
          <Button size={'small'} variant={'contained'} onClick={handleDeactivateDisclaimer} disabled={loading}>
            {t('ui_elements.continue')}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  )
}

const ToggleForm = ({ onChange }) => {
  const { t } = useTranslation()
  const [viewMode, setViewMode] = useState('by_unit')
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', pb: 4 }}>
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={(e, value) => {
          setViewMode(value)
          onChange(value)
        }}
        size="small"
        sx={{
          '& .MuiToggleButton-root': {
            fontSize: 12,
            px: 2,
            py: 0.5,
            textTransform: 'none'
          },
          width: '100%'
        }}
      >
        <ToggleButton value="by_unit" sx={{ flex: 1 }}>
          {t('bypass.modal.global.toggle.by_unit')}
        </ToggleButton>
        <ToggleButton value="by_import" sx={{ flex: 1 }}>
          {t('bypass.modal.global.toggle.by_import')}
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  )
}

interface IModalProps {
  item: IBypass | null
  onShowForm: (string) => void
  hasDisclaimer?: boolean
  children: React.ReactNode
}

export const ModalActivate = ({ children, onClose }) => {
  return (
    <Dialog
      style={{ backgroundColor: 'transparent' }}
      maxWidth='md'
      PaperProps={{
        style: {
          width: '620px',
          borderRadius: '16px'
        }
      }}
      open
    >
      <Stack gap={2} padding={4}>
        <HeaderActivate onClose={onClose} />
        {children}
      </Stack>
    </Dialog>
  )
}

const Modal: React.FC<IModalProps> = ({ item, onShowForm = () => { }, children }) => {
  const [disclaimer, setDisclaimer] = useState(item ? false : true)
  const [user] = useUser()
  return (
    <Dialog
      style={{ backgroundColor: 'transparent' }}
      maxWidth='md'
      PaperProps={{
        style: {
          width: '620px',
          borderRadius: '16px'
        }
      }}
      open
    >
      <Stack gap={2} padding={4}>
        <Header item={item} />
        {
          user?.[BYPASSES_DISCLAIMER_KEY] && disclaimer
            ? <Disclaimer onConfirm={() => setDisclaimer(false)} />
            : (
              <Stack gap={1}>
                {!item && <ToggleForm onChange={onShowForm} />}
                {
                  children
                }
              </Stack>
            )
        }
      </Stack>
    </Dialog>
  )
}

export default Modal